import "./App.css";
import "react-datepicker/dist/react-datepicker.css";
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";
import Home from "./pages/Home";
import Tool from "./pages/Tool";
import Tools from "./pages/Tools";
import AddTool from "./pages/AddTool";
import ToolsGroups from "./pages/ToolsGroups";
import AddToolsGroup from "./pages/AddToolsGroup";
import Checks from "./pages/Checks";
import Check from "./pages/Check";
import AddCheck from "./pages/AddCheck";
import AddBulkWash from "./pages/AddBulkWash";
import EditGroupCategories from "./pages/EditGroupCategories";
import EditSingleCategory from "./pages/EditSingleCategory";
import Admin from "./pages/Admin";
import QrDesktop from "./pages/QrDesktop";
import QrMobile from "./pages/QrMobile";
import Profile from "./pages/Profile";
import Licenses from "./pages/Licenses";
import AddLicense from "./pages/AddLicense";
import License from "./pages/License";
import { AuthContext } from "./helpers/AuthContext";
import { useState, useEffect } from "react";
import axios from "axios";
import Login from "./pages/Login";
import { useLocation, Navigate } from "react-router-dom"; // add Navigate and useLocation

import { loadConfig } from "./helpers/config";
import { fetchLatestRelease } from "./helpers/fetchLatestRelease";
import ReleaseNewsOverlay from "./components/ReleaseNewsOverlay";
import ChangelogOverlay from "./components/ChangelogOverlay";



function App() {
  const [authState, setAuthState] = useState({
    username: "",
    id: 0,
    status: false,
    isAdmin: false,
    canAccessLicenses: false,
  });
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [showNews, setShowNews] = useState(false);
  const [releaseInfo, setReleaseInfo] = useState({ version: "", notes: "" });
  const [menuOpen, setMenuOpen] = useState(false);
  const [appVersion, setAppVersion] = useState("");
  const [showChangelog, setShowChangelog] = useState(false);


  const [maintenance, setMaintenance] = useState(false);

  // Setup axios interceptor for global error handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if it's a network error (backend completely down) and not on login page
        if (!error.response && window.location.pathname !== '/login') {
          alert("Das System ist derzeit nicht erreichbar. Sie werden zur Login-Seite weitergeleitet.");
          // Redirect to login (keep accessToken and authState for retry)
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    async function fetchConfig() {
      let config = await loadConfig();
      setApiUrl(config.APIURL);
      setLoadingConfig(false);
      // Check maintenance mode
      try {
        const res = await fetch(`${config.APIURL}/maintenance`);
        const data = await res.json();
        if (data.maintenance) setMaintenance(true);
      } catch { }
    }
    fetchConfig();
  }, []);

  // Load the deployed app version (written to public/version.json on release)
  useEffect(() => {
    fetch("/version.json")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.version) setAppVersion(data.version);
      })
      .catch(() => {});
  }, []);

  // News overlay logic
  useEffect(() => {
    if (maintenance) return;
    async function checkReleaseNews() {
      try {
        const latest = await fetchLatestRelease();
        setReleaseInfo(latest);
        const lastSeen = localStorage.getItem("lastSeenRelease");
        if (latest.version && lastSeen !== latest.version) {
          setShowNews(true);
        }
      } catch (e) {
        // ignore fetch errors
      }
    }
    checkReleaseNews();
  }, [maintenance]);

  function handleCloseNews() {
    setShowNews(false);
    if (releaseInfo.version) {
      localStorage.setItem("lastSeenRelease", releaseInfo.version);
    }
  }

  useEffect(() => {
    if (!loadingConfig) {
      axios
        .get(`${apiUrl}/auth/auth`, {
          headers: {
            accessToken: localStorage.getItem("accessToken"),
          },
        })
        .then((response) => {
          if (response.data.error) {
            setAuthState(prevState => ({ ...prevState, status: false }));
          } else {
            setAuthState({
              username: response.data.username,
              id: response.data.id,
              status: true,
              isAdmin: response.data.isAdmin,
              canAccessLicenses: response.data.canAccessLicenses || false,
            });
          }
        })
        .catch(() => {
          setAuthState(prevState => ({ ...prevState, status: false }));
        });
    }
  }, [loadingConfig, apiUrl]);

  if (loadingConfig) {
    return <div className="loading"><span>Lade Konfiguration...</span></div>;
  }
  if (maintenance) {
    return (
      <div className="maintenance-overlay">
        <div className="maintenance-message">
          <h2>Wartungsmodus</h2>
          <p>Das System ist derzeit wegen Wartungsarbeiten deaktiviert.</p>
        </div>
      </div>
    );
  }

  // Show overlay if needed
  if (showNews && releaseInfo.version) {
    return <ReleaseNewsOverlay version={releaseInfo.version} notes={releaseInfo.notes} onClose={handleCloseNews} />;
  }

  function RequireAuth({ children }) {
    const location = useLocation();
    if (!authState.status) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return children;
  }

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    setAuthState({ username: "", id: 0, status: false, isAdmin: false, canAccessLicenses: false });
  };

  return (
    <div className="App">
      <AuthContext.Provider value={{ authState, setAuthState }}>
        <Router>
          {authState.status && (
            <div className="navbar">
              <div className="navbar-left">
                <img src="/favicon.ico" alt="logo" className="navbar-logo" />
                {appVersion && (
                  <span
                    className="navbar-version"
                    onClick={() => setShowChangelog(true)}
                    title="Changelog anzeigen"
                  >
                    v{appVersion}
                  </span>
                )}
              </div>
              <div className="menu-icon" onClick={() => setMenuOpen(!menuOpen)}>
                &#9776;
              </div>
              <div className={menuOpen ? "links open" : "links"}>
                <Link to="/" onClick={() => setMenuOpen(false)}> Home Page</Link>
                <Link to="/Tools" onClick={() => setMenuOpen(false)}>Geräte</Link>
                <Link to="/ToolsGroups" onClick={() => setMenuOpen(false)}>Gerätegruppen</Link>
                <Link to="/Checks" onClick={() => setMenuOpen(false)}>Prüfungen</Link>
                <Link to="/wash/bulk" onClick={() => setMenuOpen(false)}>Sammelwäsche</Link>
                {authState.canAccessLicenses && <Link to="/licenses" onClick={() => setMenuOpen(false)}>Führerscheine</Link>}
                <Link to="/profile" onClick={() => setMenuOpen(false)}>Profil</Link>
                {authState.isAdmin && <Link to="/admin" onClick={() => setMenuOpen(false)}>Admin</Link>}
              </div>
              <div className="auth-status">
                {authState.username}
                <button className="logout-btn" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}
          <Routes>
            <Route
              path="/login"
              element={authState.status ? <Navigate to="/" replace /> : <Login />}
            />
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/Tool/:id" element={<Tool />} />
                    <Route path="/Tools" element={<Tools />} />
                    <Route path="/Tools/Add" element={<AddTool />} />
                    <Route path="/ToolsGroups" element={<ToolsGroups />} />
                    <Route path="/ToolsGroups/add" element={<AddToolsGroup />} />
                    <Route path="/Check/:id" element={<Check />} />
                    <Route path="/Checks" element={<Checks />} />
                    <Route path="/Checks/Add/:toolId" element={<AddCheck />} />
                    <Route path="/wash/bulk" element={<AddBulkWash />} />
                    <Route path="/edit-group-categories/:id" element={<EditGroupCategories />} />
                    <Route path="/edit-group-category/:id/:idx" element={<EditSingleCategory />} />
                    <Route path="/qr-desktop" element={<QrDesktop />} />
                    <Route path="/qr-mobile" element={<QrMobile />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/licenses" element={authState.canAccessLicenses ? <Licenses /> : <Navigate to="/" replace />} />
                    <Route path="/licenses/create" element={authState.canAccessLicenses ? <AddLicense /> : <Navigate to="/" replace />} />
                    <Route path="/licenses/:id" element={authState.canAccessLicenses ? <License /> : <Navigate to="/" replace />} />
                  </Routes>
                </RequireAuth>
              }
            />
          </Routes>
        </Router>
        {showChangelog && <ChangelogOverlay onClose={() => setShowChangelog(false)} />}
      </AuthContext.Provider>
    </div>
  );
}

export default App;
